import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionsApi } from '../../lib/api';
import { getPlanLabel } from '../../lib/planLabels';
import { Button } from '../../components/ui/button';
import NotificationBell from '../../components/NotificationBell';
import ScrollToTopButton from '../../components/ScrollToTopButton';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import CurrencySwitcher from '../../components/CurrencySwitcher';
import QuotaReachedDialog from '../../components/QuotaReachedDialog';
import { useTranslation } from 'react-i18next';
import BrandLogo from '../../components/BrandLogo';
import {
    Sparkles, LayoutDashboard, Briefcase, Users, Settings,
    LogOut, Menu, X, ChevronDown, CreditCard, Shield, Archive, FileText, MessageSquareQuote, GitBranch, BarChart3, Database, DollarSign, FlaskConical
} from 'lucide-react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

export default function DashboardLayout() {
    const { t, i18n: i18nInstance } = useTranslation();
    const { user, logout, isAdmin, isBillingOnly } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPlanId, setCurrentPlanId] = useState('free');

    useEffect(() => {
        subscriptionsApi.getCurrent().then(res => {
            setCurrentPlanId(res.data?.plan || 'free');
        }).catch(() => {});
    }, []);

    // Subscribe to language changes so plan label re-renders when user switches FR/EN
    void i18nInstance.language;

    const fullNavigation = [
        { name: t('nav.dashboard'), href: '/dashboard', icon: LayoutDashboard },
        { name: t('nav.jobs'), href: '/dashboard/jobs', icon: Briefcase },
        { name: t('nav.candidates'), href: '/dashboard/candidates', icon: Users },
        { name: t('nav.pipeline'), href: '/dashboard/pipeline', icon: GitBranch },
        { name: t('nav.trash'), href: '/dashboard/trash', icon: Archive },
    ];

    const billingOnlyNavigation = [
        { name: t('nav.subscription'), href: '/dashboard/settings/subscription', icon: CreditCard },
    ];

    const navigation = isBillingOnly ? billingOnlyNavigation : fullNavigation;

    const adminNavigation = [
        { name: t('nav.admin'), href: '/dashboard/admin', icon: Shield },
        { name: t('nav.analytics'), href: '/dashboard/admin/analytics', icon: BarChart3 },
        { name: t('nav.scoring_cache'), href: '/dashboard/admin/scoring-cache', icon: Database },
        { name: t('nav.scoring_compare', 'Comparaison V1/V2'), href: '/dashboard/admin/scoring-compare', icon: FlaskConical },
        { name: t('nav.cv_extraction_cache'), href: '/dashboard/admin/cv-extraction-cache', icon: Database },
        { name: t('nav.llm_journal', 'Journal LLM'), href: '/dashboard/admin/llm-journal', icon: DollarSign },
    ];

    const testimonialsNavItem = { name: t('nav.testimonials'), href: '/dashboard/testimonials', icon: MessageSquareQuote };

    const auditAllowed = user && ['admin', 'manager', 'billing_admin'].includes(user.role);
    const auditNavItem = { name: t('nav.audit'), href: '/dashboard/admin/audit', icon: FileText };

    const handleLogout = async () => {
        await logout();
        navigate('/');
    };

    const isActive = (href) => {
        if (href === '/dashboard') {
            return location.pathname === '/dashboard';
        }
        return location.pathname.startsWith(href);
    };

    return (
        <div className="min-h-screen bg-[#F9FAFB]" data-testid="dashboard-layout">
            {/* Mobile sidebar overlay */}
            {sidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Sidebar */}
            <aside className={`
                fixed top-0 left-0 bottom-0 w-64 bg-white border-r border-[#E5E7EB] z-50
                transform transition-transform duration-200 lg:translate-x-0
                ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
            `}>
                <div className="h-full flex flex-col">
                    {/* Logo */}
                    <div className="h-16 flex items-center justify-between px-6 border-b border-[#E5E7EB]">
                        <Link to="/dashboard" className="flex items-center gap-3">
                            <BrandLogo size={36} />
                            <span className="text-xl font-bold text-[#0A0A0A] font-['Outfit']">GUIRETAL</span>
                        </Link>
                        <button 
                            onClick={() => setSidebarOpen(false)}
                            className="lg:hidden text-[#9CA3AF] hover:text-[#0A0A0A]"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 py-6 px-4 space-y-1 overflow-y-auto">
                        {navigation.map((item) => (
                            <Link
                                key={item.name}
                                to={item.href}
                                onClick={() => setSidebarOpen(false)}
                                className={`
                                    flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                                    ${isActive(item.href) 
                                        ? 'bg-[#002FA7]/10 text-[#002FA7] font-medium' 
                                        : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0A0A]'
                                    }
                                `}
                                data-testid={`nav-${item.name.toLowerCase().replace(/[^a-z]/g, '-')}`}
                            >
                                <item.icon className="w-5 h-5" />
                                {item.name}
                            </Link>
                        ))}

                        {isAdmin && (
                            <>
                                <div className="h-px bg-[#E5E7EB] my-4" />
                                {adminNavigation.map((item) => (
                                    <Link
                                        key={item.name}
                                        to={item.href}
                                        onClick={() => setSidebarOpen(false)}
                                        className={`
                                            flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                                            ${isActive(item.href) 
                                                ? 'bg-[#002FA7]/10 text-[#002FA7] font-medium' 
                                                : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0A0A]'
                                            }
                                        `}
                                        data-testid={`nav-${item.name.toLowerCase()}`}
                                    >
                                        <item.icon className="w-5 h-5" />
                                        {item.name}
                                    </Link>
                                ))}
                            </>
                        )}

                        {/* Témoignages: visible to every authenticated user */}
                        <Link
                            key={testimonialsNavItem.name}
                            to={testimonialsNavItem.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`
                                flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                                ${isActive(testimonialsNavItem.href)
                                    ? 'bg-[#002FA7]/10 text-[#002FA7] font-medium'
                                    : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0A0A]'
                                }
                            `}
                            data-testid="nav-temoignages"
                        >
                            <testimonialsNavItem.icon className="w-5 h-5" />
                            {testimonialsNavItem.name}
                        </Link>

                        {auditAllowed && (
                            <>
                                {!isAdmin && <div className="h-px bg-[#E5E7EB] my-4" />}
                                <Link
                                    key={auditNavItem.name}
                                    to={auditNavItem.href}
                                    onClick={() => setSidebarOpen(false)}
                                    className={`
                                        flex items-center gap-3 px-4 py-3 rounded-lg transition-colors
                                        ${isActive(auditNavItem.href) 
                                            ? 'bg-[#002FA7]/10 text-[#002FA7] font-medium' 
                                            : 'text-[#4B5563] hover:bg-[#F3F4F6] hover:text-[#0A0A0A]'
                                        }
                                    `}
                                    data-testid={`nav-${auditNavItem.name.toLowerCase()}`}
                                >
                                    <auditNavItem.icon className="w-5 h-5" />
                                    {auditNavItem.name}
                                </Link>
                            </>
                        )}
                    </nav>

                    {/* Subscription card */}
                    <div className="p-4">
                        <div className="bg-[#F3F4F6] rounded-lg p-4">
                            <div className="flex items-center gap-2 text-[#4B5563] text-sm mb-2">
                                <CreditCard className="w-4 h-4" />
                                {t('nav.current_subscription')}
                            </div>
                            <p className="font-semibold text-[#0A0A0A]">{getPlanLabel(currentPlanId)}</p>
                            <Link to="/dashboard/settings/subscription">
                                <Button variant="outline" size="sm" className="mt-3 w-full">
                                    {user?.role === 'recruiter' ? t('common.view') : currentPlanId === 'enterprise' ? t('nav.manage') : t('nav.upgrade')}
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </aside>

            {/* Main content */}
            <div className="lg:pl-64">
                {/* Header */}
                <header className="h-16 bg-white border-b border-[#E5E7EB] sticky top-0 z-30">
                    <div className="h-full px-4 sm:px-6 flex items-center justify-between gap-2">
                        <button 
                            onClick={() => setSidebarOpen(true)}
                            className="lg:hidden text-[#4B5563] hover:text-[#0A0A0A]"
                            data-testid="mobile-menu-btn"
                        >
                            <Menu className="w-6 h-6" />
                        </button>

                        <div className="flex-1" />

                        <div className="flex items-center gap-2 sm:gap-4">
                            <LanguageSwitcher />
                            <CurrencySwitcher />
                            <NotificationBell />

                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button className="flex items-center gap-3 hover:bg-[#F3F4F6] px-3 py-2 rounded-lg transition-colors">
                                        <div className="w-8 h-8 bg-[#002FA7] rounded-full flex items-center justify-center text-white font-medium">
                                            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                                        </div>
                                        <div className="hidden sm:block text-left">
                                            <p className="text-sm font-medium text-[#0A0A0A]">{user?.name}</p>
                                            <p className="text-xs text-[#9CA3AF]">{user?.role}</p>
                                        </div>
                                        <ChevronDown className="w-4 h-4 text-[#9CA3AF]" />
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuItem asChild>
                                        <Link to="/dashboard/settings/profile" className="flex items-center gap-2" data-testid="settings-link">
                                            <Settings className="w-4 h-4" />
                                            {t('nav.settings')}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuItem asChild>
                                        <Link to="/dashboard/settings/subscription" className="flex items-center gap-2">
                                            <CreditCard className="w-4 h-4" />
                                            {t('nav.subscription')}
                                        </Link>
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem 
                                        onClick={handleLogout}
                                        className="text-[#EF4444] focus:text-[#EF4444]"
                                        data-testid="logout-btn"
                                    >
                                        <LogOut className="w-4 h-4 mr-2" />
                                        {t('common.logout')}
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </div>
                    </div>
                </header>

                {/* Page content */}
                <main className="p-4 sm:p-6">
                    <Outlet />
                </main>
            </div>

            <ScrollToTopButton />
            <QuotaReachedDialog />
        </div>
    );
}
